import merge from 'deepmerge';
import clone from 'lodash/clone';
import clamp from 'lodash/clamp';
import setWith from 'lodash/setWith';
import cloneDeep from 'lodash/cloneDeep';
import {
  ACQUIRE_POPUP_SPINNER_LOADING,
  DECREMENT_PLAN_IMPACT_SPINNER_LOADING,
  DECREMENT_WHAT_IFS_SPINNER_LOADING,
  INCREMENT_PLAN_IMPACT_SPINNER_LOADING,
  INCREMENT_WHAT_IFS_SPINNER_LOADING,
  OPL_MONTE_CARLO_ACTIVE,
  SET_WHAT_IFS_BODY,
  OPTIMIZE_COMPARE_GOALS,
  RELEASE_POPUP_SPINNER_LOADING,
  SET_COMPARE_GOALS_OPEN,
  SET_CURRENT_VIEW_ID,
  SET_DETAILED_ASSET_ALLOCATION_OPEN,
  SET_ERRORS,
  SET_MODEL_PORTFOLIO_GROUPS,
  SET_OPEN,
  SET_SELECTED_SECTION,
  SET_VISIBLE,
  SET_WHAT_IFS_DATA,
  UPDATE_COMPARE_GOALS_DATA,
  UPDATE_COMPARE_GOALS_PLAN_IMPACTS,
  UPDATE_WHAT_IFS_DATA,
} from './WhatIfsActions';
import { isNonEmptyString } from '../../js/app/react/validators';
import { BASE_GOAL_OBJECT } from './WhatIfsConstants';

const defined = obj => obj || {};

const transformWhatIfsArrayToMap = whatIfsArray => {
  const map = {};
  whatIfsArray.forEach(whatIf => {
    map[whatIf.WhatIfKey] = whatIf;
  });
  return map;
};

const initialState = {
  modelPortfolioGroups: [],
  isMonteCarloActive: false,
  whatIfsBody: null,
};

export default (state = initialState, action) => {
  const { type } = action;

  switch (type) {
    case SET_COMPARE_GOALS_OPEN: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          compareGoals: {
            ...defined(state[action.clientId]).compareGoals,
            open: action.compareGoalsOpen,
          },
        },
      };
    }
    case SET_DETAILED_ASSET_ALLOCATION_OPEN: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          detailedAssetAllocationOpen: action.detailedAssetAllocationOpen,
        },
      };
    }
    case SET_VISIBLE: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          visible: action.visible,
        },
      };
    }
    case SET_OPEN: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          open: action.open,
        },
      };
    }
    case SET_ERRORS: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          errors: action.errors,
        },
      };
    }
    case INCREMENT_WHAT_IFS_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          loading: (clientState.loading || 0) + 1,
        },
      };
    }
    case DECREMENT_WHAT_IFS_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      const loading = (clientState.loading || 0) - 1;
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          loading: clamp(loading, 0, loading),
        },
      };
    }
    case INCREMENT_PLAN_IMPACT_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          planImpactLoading: (clientState.planImpactLoading || 0) + 1,
        },
      };
    }
    case DECREMENT_PLAN_IMPACT_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      const planImpactLoading = (clientState.planImpactLoading || 0) - 1;
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          planImpactLoading: clamp(planImpactLoading, 0, planImpactLoading),
        },
      };
    }
    case ACQUIRE_POPUP_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          popupLoading: (clientState.popupLoading || 0) + 1,
        },
      };
    }
    case RELEASE_POPUP_SPINNER_LOADING: {
      const clientState = defined(state[action.clientId]);
      const popupLoading = (clientState.popupLoading || 0) - 1;
      return {
        ...state,
        [action.clientId]: {
          ...clientState,
          popupLoading: clamp(popupLoading, 0, popupLoading),
        },
      };
    }
    case SET_CURRENT_VIEW_ID: {
      return {
        ...state,
        whatIfsBody: null,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          [action.goalId]: {
            ...(defined(state[action.clientId])[action.goalId] || BASE_GOAL_OBJECT),
            currentViewId: action.currentViewId,
          },
        },
      };
    }
    case SET_SELECTED_SECTION: {
      return {
        ...state,
        [action.clientId]: {
          ...defined(state[action.clientId]),
          [action.goalId]: {
            ...defined(defined(state[action.clientId])[action.goalId]),
            selectedSection: action.selectedSection,
          },
        },
      };
    }
    case SET_WHAT_IFS_DATA: {
      const { clientId, goalId, data } = action;
      const clientState = defined(state[clientId]);
      const goalState = defined(clientState[goalId]);
      return {
        ...state,
        [clientId]: {
          ...clientState,
          [goalId]: {
            ...goalState,
            whatIfsSections: data,
            whatIfsTemplateSections: clone(data),
          },
        },
      };
    }
    case UPDATE_COMPARE_GOALS_DATA: {
      const { clientId, path, value } = action;
      const clientState = defined(state[clientId]);
      const compareGoalsState = defined(clientState.compareGoals);
      // shallow clone is required because redux will do a shallow compare
      // to determine what are the props that changed
      let data = clone(defined(compareGoalsState.data));
      if (isNonEmptyString(path)) {
        setWith(data, path, value, (nsValue, key, nsObjectArg) => {
          const nsObject = nsObjectArg;
          nsObject[key] = clone(nsValue);
          return nsObject[key];
        });
      } else {
        data = cloneDeep(value);
      }
      return {
        ...state,
        [clientId]: {
          ...clientState,
          compareGoals: {
            ...compareGoalsState,
            data,
          },
        },
      };
    }
    case OPTIMIZE_COMPARE_GOALS: {
      const { clientId, whatIfs, goalId, whatIfType, whatIfKey } = action;
      const clientState = defined(state[clientId]);
      const goalState = defined(clientState[goalId]);
      const compareGoalsState = defined(clientState.compareGoals);
      const dataState = defined(compareGoalsState.data);
      const goalsAdditionalSavingsWhatIfs = dataState.GoalsAdditionalSavingsWhatIfs || [];
      const whatIfsSections = clone(defined(goalState.whatIfsSections));
      const whatIfsMap = transformWhatIfsArrayToMap(whatIfs);
      const allocatedCashflowSelector = (key, WhatIf) => {
        if (whatIfsMap[key]) {
          return whatIfsMap[key].AllocatedCashflow;
        }
        return WhatIf.AllocatedCashflow;
      };

      const compareGoalsWhatIfSelector = (key, WhatIfPanelItem) => {
        if (whatIfsMap[key]) {
          return whatIfsMap[key];
        }
        return WhatIfPanelItem.WhatIf;
      };

      Object.keys(whatIfsSections).forEach(key => {
        const section = whatIfsSections[key] || [];
        section.forEach((whatIf, index) => {
          if (whatIf.WhatIf.WhatIfType === whatIfType) {
            section[index] = {
              ...whatIf,
              WhatIf: {
                ...whatIf.WhatIf,
                AllocatedCashflow: allocatedCashflowSelector(whatIfKey, whatIf.WhatIf),
              },
            };
          }
        });
      });

      return {
        ...state,
        [clientId]: {
          ...clientState,
          [goalId]: {
            ...goalState,
            whatIfsSections,
          },
          compareGoals: {
            ...compareGoalsState,
            data: {
              ...dataState,
              GoalsAdditionalSavingsWhatIfs: goalsAdditionalSavingsWhatIfs.map(asavWhatIf => ({
                ...asavWhatIf,
                WhatIfPanelItem: {
                  ...asavWhatIf.WhatIfPanelItem,
                  WhatIf: compareGoalsWhatIfSelector(
                    asavWhatIf.WhatIfPanelItem.WhatIf.WhatIfKey,
                    asavWhatIf.WhatIfPanelItem,
                  ),
                },
              })),
            },
          },
        },
      };
    }
    case UPDATE_COMPARE_GOALS_PLAN_IMPACTS: {
      const {
        clientId,
        impacts,
        goalId,
        whatIfKey,
        whatIfType,
        availableCashflow,
        maxCashflow,
      } = action;

      const clientState = defined(state[clientId]);
      const goalState = defined(clientState[goalId]);
      const compareGoalsState = defined(clientState.compareGoals);
      const dataState = defined(compareGoalsState.data);
      const goalsAdditionalSavingsWhatIfs = dataState.GoalsAdditionalSavingsWhatIfs || [];

      const whatIfsSections = clone(defined(goalState.whatIfsSections));

      Object.keys(whatIfsSections).forEach(key => {
        const section = whatIfsSections[key] || [];
        section.forEach((whatIf, index) => {
          if (whatIf.WhatIf.WhatIfType === whatIfType) {
            const nullableImpact = impacts.find(impact => impact.Key === whatIfKey);
            if (nullableImpact) {
              section[index] = {
                ...whatIf,
                Impact: nullableImpact.Value.map(impact => ({
                  label: impact.Label,
                  current: impact.Current,
                  proposed: impact.Proposed,
                })),
              };
            }
          }
        });
      });

      return {
        ...state,
        [clientId]: {
          ...clientState,
          [goalId]: {
            ...goalState,
            whatIfsSections,
          },
          compareGoals: {
            ...compareGoalsState,
            data: {
              ...dataState,
              AvailableCashflow: availableCashflow,
              MaxCashflow: maxCashflow,
              GoalsAdditionalSavingsWhatIfs: goalsAdditionalSavingsWhatIfs.map(asavWhatIf => ({
                ...asavWhatIf,
                WhatIfPlanImpact: [
                  impacts.find(({ Key }) => Key === asavWhatIf.WhatIfPlanImpact[0].Key),
                ],
              })),
            },
          },
        },
      };
    }
    case SET_MODEL_PORTFOLIO_GROUPS: {
      const { groups } = action;

      return {
        ...state,
        modelPortfolioGroups: groups,
      };
    }
    case UPDATE_WHAT_IFS_DATA: {
      const { clientId, goalId, whatIfType, data } = action;
      const clientState = defined(state[clientId]);
      const goalState = defined(clientState[goalId]);
      const whatIfsSections = clone(defined(goalState.whatIfsSections));

      Object.keys(whatIfsSections).forEach(key => {
        const section = whatIfsSections[key] || [];
        section.forEach((whatIf, index) => {
          if (whatIf.WhatIf.WhatIfType === whatIfType) {
            section[index] = merge(whatIf, data, { arrayMerge: (a, b) => b });
          }
        });
      });

      return {
        ...state,
        [clientId]: {
          ...clientState,
          [goalId]: {
            ...goalState,
            whatIfsSections,
          },
        },
      };
    }

    case OPL_MONTE_CARLO_ACTIVE: {
      return {
        ...state,
        isMonteCarloActive: action.payload,
      };
    }

    case SET_WHAT_IFS_BODY: {
      return {
        ...state,
        whatIfsBody: action.payload,
      };
    }

    default:
      return state;
  }
};
